/**
 * Resource Board catalog API contracts (SCH-3172, plan
 * `docs/plans/2026-10-15-0915-feat-resource-board-config-plan.md`).
 *
 * The dashboard's Features → Resource Board section reads and orders the
 * school's stored catalog of Blackbaud resource-board items
 * (`ResourceBoardItem`). Every route is under
 * `/v1/schools/:school_public_id/resource-board/catalog` and is for an admin of
 * that school or a super-admin.
 *
 * Items are named by their `ResourceBoardItem.publicId`. `bbItemId` is
 * Blackbaud's own resource id, shown for reference only.
 *
 * The tile COLUMNS setting is not here: it is the `resourceBoard` global
 * feature (`ResourceBoardFeatureConfigSchema`), written through
 * `/v1/system/app-config/global-features/resourceBoard`.
 */

import { z } from 'zod'

/** The three Blackbaud persona boards, by the name the dashboard shows. */
export const ResourceBoardPersonaNameSchema = z.enum(['faculty', 'parent', 'student'])
export type ResourceBoardPersonaName = z.infer<typeof ResourceBoardPersonaNameSchema>

/**
 * Whether an item is on one persona's board. `lastSeenAt` is the last time it
 * was seen there (ISO 8601); `null` means it is not on that board — never seen,
 * or dropped by a refresh whose answer for that persona no longer had it.
 */
export const ResourceBoardPresenceSchema = z.object({
  onBoard: z.boolean(),
  lastSeenAt: z.string().nullable()
})
export type ResourceBoardPresence = z.infer<typeof ResourceBoardPresenceSchema>

/** One stored catalog item. */
export const ResourceBoardCatalogItemSchema = z.object({
  publicId: z.string(),
  /** Blackbaud's resource id. */
  bbItemId: z.number().int(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  /** As Blackbaud returns it. May be protocol-relative (`//...`): prefix `https:` to display. */
  photo: z.string().nullable(),
  url: z.string().nullable(),
  /** "Detail Page" | "Direct Link", as Blackbaud returns it. */
  type: z.string().nullable(),
  boards: z.object({
    faculty: ResourceBoardPresenceSchema,
    parent: ResourceBoardPresenceSchema,
    student: ResourceBoardPresenceSchema
  }),
  /** 1-based position in the shared order; `null` = unplaced (follows the placed items, A–Z by title). */
  sortOrder: z.number().int().nullable()
})
export type ResourceBoardCatalogItem = z.infer<typeof ResourceBoardCatalogItemSchema>

// ============================================================================
// GET /v1/schools/:school_public_id/resource-board/catalog
// ============================================================================

/** The school's catalog, already in display order (placed by `sortOrder`, then unplaced A–Z). */
export const GetResourceBoardCatalogResponseSchema = z.object({
  message: z.string(),
  data: z.array(ResourceBoardCatalogItemSchema)
})
export type GetResourceBoardCatalogResponse = z.infer<typeof GetResourceBoardCatalogResponseSchema>

// ============================================================================
// POST /v1/schools/:school_public_id/resource-board/catalog/refresh
// ============================================================================

/**
 * What one persona call returned during a refresh:
 *  - `ok` — Blackbaud answered; `count` distinct items. Items no longer on
 *    that board are marked off it.
 *  - `not-visible` — the school's sync account holds no role in that persona
 *    family, so Blackbaud refuses it (400 "doesn't have persona"). Existing
 *    rows are kept; app loads fill that board in.
 *  - `error` — any other failure (including no usable sync token). Existing
 *    rows are kept.
 */
export const ResourceBoardRefreshStatusSchema = z.enum(['ok', 'not-visible', 'error'])
export type ResourceBoardRefreshStatus = z.infer<typeof ResourceBoardRefreshStatusSchema>

export const ResourceBoardPersonaRefreshOutcomeSchema = z.object({
  status: ResourceBoardRefreshStatusSchema,
  /** Distinct items Blackbaud returned; 0 unless `status` is `ok`. */
  count: z.number().int()
})
export type ResourceBoardPersonaRefreshOutcome = z.infer<typeof ResourceBoardPersonaRefreshOutcomeSchema>

export const RefreshResourceBoardCatalogResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    /** When the refresh ran (ISO 8601). */
    refreshedAt: z.string(),
    personas: z.object({
      faculty: ResourceBoardPersonaRefreshOutcomeSchema,
      parent: ResourceBoardPersonaRefreshOutcomeSchema,
      student: ResourceBoardPersonaRefreshOutcomeSchema
    }),
    /** The catalog after the refresh, in display order. */
    items: z.array(ResourceBoardCatalogItemSchema)
  })
})
export type RefreshResourceBoardCatalogResponse = z.infer<typeof RefreshResourceBoardCatalogResponseSchema>

// ============================================================================
// PUT /v1/schools/:school_public_id/resource-board/catalog/order
// ============================================================================

/**
 * The full shared order. Listed items get `sortOrder` 1..n in this order and
 * every other item of the school becomes unplaced. `[]` resets the board to
 * alphabetical. Every id must be one of this school's catalog items, or the
 * whole write is refused (400) and nothing changes.
 */
export const UpdateResourceBoardOrderRequestSchema = z.object({
  orderedPublicIds: z
    .array(z.string().min(1))
    .max(2000)
    .refine(ids => new Set(ids).size === ids.length, { message: 'orderedPublicIds must not repeat an id' })
})
export type UpdateResourceBoardOrderRequest = z.infer<typeof UpdateResourceBoardOrderRequestSchema>

/** The catalog after the write, in display order. */
export const UpdateResourceBoardOrderResponseSchema = GetResourceBoardCatalogResponseSchema
export type UpdateResourceBoardOrderResponse = GetResourceBoardCatalogResponse
